import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { QuestionLayout } from '../layout/QuestionLayout';
import { HeaderSection } from '../layout/HeaderSection';
import { PageContainer } from '../layout/PageContainer';
import { getTestId } from '../../utils/testUtils';
import type { Flow, QuestionFlowItemSummary, ResponsePayload } from '../../types';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { QuestionFlowLoadingState } from './QuestionFlowLoadingState';
import { QuestionFlowEmptyState } from './QuestionFlowEmptyState';
import { useAuth } from '../../contexts/AuthContext';
import { useQuizAttemptManager, type SubmitQuizOptions } from './useQuizAttemptManager';
import { InvalidFlow } from './Invalidflow';
import { AccessDenied } from './AccessDenied';
import { isQuestionResponseValid } from 'utils/questionResponseValidation';
import { QuestionFlowStage } from './QuestionFlowStage';
import { useAnswerReferences } from './useAnswerReferences';
import { AnswerReferenceContext } from 'utils/answerReferences';
import { useFlowPrerequisiteGuard } from './useFlowPrerequisiteGuard';
import { QuizIntroScreen } from './QuizIntroScreen';
import { QuizFlowInitialBootstrap } from './types';
import { FlowThemeLayer, FlowThemeNavBand, ThemeContextProvider, resolveFlowTheme, type FlowThemeProgress } from './themes';
import { useFlowThemeTransitionReady } from './themes/FlowThemeTransition';

import { useLearnerThemeContext } from './themes/useLearnerThemeContext';
import { createThemeCompletionId } from './themes/themeCompletionEvents';

interface QuizFlowProps {
  flowName: string;
  courseId?: number | null;
  initialFlow?: Flow | null;
  initialBootstrap?: QuizFlowInitialBootstrap | null;
}

type SubmitQuestionOptions = {
  advance?: boolean;
};

const QuizFlow: React.FC<QuizFlowProps> = ({
  flowName,
  courseId = null,
  initialFlow = null,
  initialBootstrap = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const pendingTransitionDirectionRef = useRef<1 | -1 | null>(null);
  const [transitionDirection, setTransitionDirection] = useState<1 | -1>(1);
  const resolvedInitialFlow = initialBootstrap?.flow ?? initialFlow;
  const { guardLoading, blockingFlow, lockedCourseId, errorMessage: guardError } = useFlowPrerequisiteGuard(flowName, {
    courseId,
    initialResult: initialBootstrap?.prerequisite ?? null,
  });

  const {
    loading,
    error,
    autosaveError,
    isAccessDenied,
    isQuizEntryBlocked,
    hasStartedAttempt,
    flow,
    intro,
    attempt,
    questions,
    responsesByQuestion,
    isSubmitting,
    isSavingResponse,
    startOrResumeAttempt,
    submitQuestionResponse,
    submitQuiz,
    clearAutosaveError,
    currentQuestionIndex,
    currentQuestion,
    goToPreviousQuestion,
  } = useQuizAttemptManager({
    flowName,
    initialFlow: resolvedInitialFlow,
    initialBootstrap,
    user,
    courseId,
  });

  const flowItemsByQuestionId = useMemo<Record<number, QuestionFlowItemSummary>>(() => {
    const map: Record<number, QuestionFlowItemSummary> = {};

    questions.forEach((question, index) => {
      if (typeof question?.id !== 'number') {
        return;
      }

      const flowItemId =
        typeof question.flow_item_id === 'number' ? question.flow_item_id : question.id;
      map[question.id] = {
        id: flowItemId,
        question: question.id,
        question_id: question.id,
        order: index + 1,
        is_required: question.is_required !== false,
        conditions: null,
      };
    });

    return map;
  }, [questions]);

  const answeredCount = useMemo(
    () =>
      questions.filter((question) => {
        const questionId = question.id;
        if (typeof questionId !== 'number') {
          return false;
        }

        const response = responsesByQuestion[questionId];
        if (!response || response.question !== questionId || response.is_completed !== true) {
          return false;
        }

        return isQuestionResponseValid(question, { draft: response });
      }).length,
    [questions, responsesByQuestion],
  );

  const activeQuestionIndex =
    questions.length > 0
      ? Math.min(currentQuestionIndex, Math.max(questions.length - 1, 0))
      : 0;
  const questionToRender = currentQuestion ?? questions[activeQuestionIndex] ?? null;
  const questionIndex = questionToRender
    ? questions.findIndex((question) => question.id === questionToRender.id)
    : -1;
  const questionId = questionToRender?.id;
  const questionResponse = questionId ? responsesByQuestion[questionId] : undefined;
  const questionFlowItem = questionId ? flowItemsByQuestionId[questionId] : undefined;
  const answerReferenceState = useAnswerReferences({
    question: hasStartedAttempt ? questionToRender : null,
    flowName: flow?.name || flowName,
    flowItemId: questionFlowItem?.id,
    source: attempt?.id ? { quizAttemptId: attempt.id } : null,
    responses: responsesByQuestion,
    enabled: hasStartedAttempt && !loading && !guardLoading,
    savesPending: isSavingResponse,
  });
  const isLastQuestion = questionIndex === questions.length - 1;
  const canGoToPrevious = currentQuestionIndex > 0;
  const upcomingQuestionIds = useMemo(
    () =>
      questions
        .slice(Math.min(currentQuestionIndex + 1, questions.length))
        .map((question) => question.id)
        .filter((candidate): candidate is number => typeof candidate === 'number')
        .slice(0, 2),
    [currentQuestionIndex, questions],
  );

  const progressValue = useMemo(() => {
    if (!questions.length) {
      return 0;
    }
    return Math.round((answeredCount / questions.length) * 100);
  }, [answeredCount, questions.length]);
  const [isThemeReady, setIsThemeReady] = useState(false);
  // The theme runs from the intro through the questions; the results page
  // takes it over (same theme, no entrance) and plays the finale.
  const themeProgress = useMemo<FlowThemeProgress | null>(() => {
    if (!flow) {
      return null;
    }
    if (hasStartedAttempt && questions.length) {
      return { step: Math.max(activeQuestionIndex, 0), total: questions.length, complete: false };
    }
    return { step: 0, total: Math.max(questions.length || intro?.total_questions || 0, 1), complete: false };
  }, [activeQuestionIndex, flow, hasStartedAttempt, intro?.total_questions, questions.length]);
  const isPassOnlyFlow =
    Boolean(intro?.is_pass_only) ||
    intro?.flow?.passing_score_percentage === null ||
    flow?.passing_score_percentage === null;

  const resolvedFlowId =
    typeof flow?.id === 'number'
      ? flow.id
      : typeof initialFlow?.id === 'number'
      ? initialFlow.id
      : typeof attempt?.flow === 'number'
      ? attempt.flow
      : null;
  const hasRedirectedForMaxAttemptsRef = useRef(false);

  useEffect(() => {
    if (!isQuizEntryBlocked || loading || hasRedirectedForMaxAttemptsRef.current) {
      return;
    }

    if (typeof resolvedFlowId !== 'number') {
      return;
    }

    hasRedirectedForMaxAttemptsRef.current = true;
    navigate(`/quiz-results/${resolvedFlowId}${typeof courseId === 'number' ? `?courseId=${courseId}` : ''}`, {
      replace: true,
      state: { flowName: flow?.name ?? initialFlow?.name ?? flowName, courseId },
    });
  }, [courseId, flow?.name, flowName, initialFlow?.name, isQuizEntryBlocked, loading, navigate, resolvedFlowId]);

  const completionContext = useMemo(() => {
    const responsesByKey: Record<string, ResponsePayload | undefined> = {};

    questions.forEach((question) => {
      if (!question || typeof question.id !== 'number' || typeof question.key !== 'string') {
        return;
      }
      responsesByKey[question.key] = responsesByQuestion[question.id];
    });

    return {
      responsesByKey,
      targetDate: null,
    };
  }, [questions, responsesByQuestion]);

  const handleQuestionSubmit = useCallback(
    (response: ResponsePayload, options?: SubmitQuestionOptions) => {
      if (!response?.question) {
        return;
      }

      const questionIndex = questions.findIndex((question) => question.id === response.question);
      const defaultAdvance = options?.advance ?? true;
      const shouldSubmitQuiz = questionIndex >= 0 && questionIndex === questions.length - 1 && defaultAdvance;
      const nextOptions: SubmitQuestionOptions = {
        ...options,
        advance: shouldSubmitQuiz ? false : defaultAdvance,
      };

      if (nextOptions.advance !== false) {
        pendingTransitionDirectionRef.current = 1;
        setTransitionDirection(1);
      }

      const submitQuestionPromise = submitQuestionResponse(response, nextOptions);

      if (!shouldSubmitQuiz) {
        return submitQuestionPromise.catch((saveError) => {
          console.error('Failed to cache quiz response', saveError);
        });
      }

      submitQuestionPromise.catch((saveError) => {
        console.error('Failed to cache quiz response before submit', saveError);
      });

      const submitOptions: SubmitQuizOptions = { includeQuestionReview: true };

      return submitQuiz(submitOptions)
        .then((result) => {
          const resolvedFlow = flow ?? initialFlow;
          const resolvedFlowId =
            typeof resolvedFlow?.id === 'number' ? resolvedFlow.id : attempt?.flow;
          if (typeof resolvedFlowId !== 'number') {
            throw new Error('Unable to resolve quiz flow id for results.');
          }

          navigate(`/quiz-results/${resolvedFlowId}${typeof courseId === 'number' ? `?courseId=${courseId}` : ''}`, {
            state: {
              quizResults: result,
              flowName: resolvedFlow?.name ?? flowName,
              courseId,
              // The results page continues this quiz's theme and plays its finale.
              flowTheme: resolvedFlow?.theme ?? null,
              themeTotal: questions.length,
              themeCompletionId: createThemeCompletionId(),
              themeModule: resolvedFlow ? {
                key: resolvedFlow.key, name: resolvedFlow.name, title: resolvedFlow.title,
                estimated_duration_minutes: resolvedFlow.estimated_duration_minutes,
                theme_content: resolvedFlow.theme_content,
              } : undefined,
            },
          });
        })
        .catch((submissionError) => {
          console.error('Failed to submit quiz', submissionError);
        });
    },
    [attempt?.flow, courseId, flow, flowName, initialFlow, navigate, questions, submitQuestionResponse, submitQuiz],
  );

  const handlePreviousClick = useCallback(() => {
    pendingTransitionDirectionRef.current = -1;
    setTransitionDirection(-1);
    goToPreviousQuestion();
  }, [goToPreviousQuestion]);

  const handleStartQuizAttempt = useCallback(() => {
    startOrResumeAttempt().catch((startError) => {
      console.error('Failed to start or resume quiz attempt', startError);
    });
  }, [startOrResumeAttempt]);

  const handleStartVoiceMode = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set('mode', 'voice');
    if (typeof courseId === 'number' && !params.has('courseId') && !location.pathname.startsWith('/courses/')) {
      params.set('courseId', String(courseId));
    }
    navigate(`${location.pathname}?${params.toString()}`);
  }, [courseId, location.pathname, location.search, navigate]);
  // Kept at the same position in every themed render below, so the scene runs
  // on from the intro through starting the attempt and into the questions.
  const dynamicThemeContext = useLearnerThemeContext({
    flow, question: hasStartedAttempt ? questionToRender : null, firstName: user?.first_name,
    position: activeQuestionIndex,
    total: questions.length || intro?.total_questions || 0, completed: answeredCount,
    complete: false, status: answeredCount > 0 ? 'in_progress' : 'not_started', responses: responsesByQuestion,
    introduction: !hasStartedAttempt, loading: guardLoading || loading, saving: isSubmitting,
    error: Boolean(error || autosaveError), direction: transitionDirection,
    runKey: `${flowName}:quiz:${attempt?.id ?? 'pending'}`,
  });
  const backToThemeCourse = useCallback(() => {
    if (courseId !== null) navigate(`/training?courseId=${courseId}#courses`);
  }, [courseId, navigate]);
  const themeLayer = (
    <FlowThemeLayer theme={flow?.theme} themeContext={dynamicThemeContext} onBackToCourse={courseId !== null ? backToThemeCourse : undefined} progress={themeProgress} onReadyChange={setIsThemeReady} />
  );
  const themeDefinition = resolveFlowTheme(flow?.theme);
  const entryUnavailable = Boolean(lockedCourseId || guardError || blockingFlow || isAccessDenied
    || error || (hasStartedAttempt && !questions.length));
  useFlowThemeTransitionReady(
    !guardLoading && !loading && !isQuizEntryBlocked
      && (entryUnavailable || !hasStartedAttempt || Boolean(questionToRender)),
    { unthemed: entryUnavailable },
  );

  if (guardLoading || loading) {
    return (
      <ThemeContextProvider value={dynamicThemeContext}>
        {themeLayer}
        <QuestionFlowLoadingState frosted={Boolean(themeProgress)} />
      </ThemeContextProvider>
    );
  }

  if (lockedCourseId) {
    return <Navigate to={`/training?courseId=${lockedCourseId}#courses`} replace state={{ courseLocked: true }} />;
  }

  if (guardError) {
    return <AccessDenied message={guardError} courseId={courseId} onRetry={() => window.location.reload()} />;
  }

  if (blockingFlow) {
    return <InvalidFlow flow={blockingFlow} courseId={courseId} />;
  }

  if (isAccessDenied) {
    return <AccessDenied />;
  }

  if (isQuizEntryBlocked && typeof resolvedFlowId === 'number') {
    return <QuestionFlowLoadingState />;
  }

  if (error) {
    return <AccessDenied message={error} />;
  }

  if (!hasStartedAttempt) {
    const isIntroThemed = Boolean(themeProgress);
    return (
      <ThemeContextProvider value={dynamicThemeContext}>
        {themeLayer}
        {/* Over a theme the intro card turns to frosted glass and the site
            navigation gets a frosted band, so both stay legible. */}
        <div
          className="flow-theme-scope min-h-screen w-full"
          data-testid="quiz-intro-layout"
          data-flow-theme={isIntroThemed ? themeDefinition.id : undefined}
          data-flow-card-glass={isIntroThemed ? themeDefinition.cardGlass : undefined}
        >
          {isIntroThemed ? <FlowThemeNavBand /> : null}
          <header>
            <PageContainer className="pt-3 sm:pt-4 lg:pt-6">
              <HeaderSection />
            </PageContainer>
          </header>
          <PageContainer className="pb-2 pt-4 sm:pt-6">
            <QuizIntroScreen
              flowName={flow?.title || flow?.name || flowName}
              intro={intro}
              isStarting={loading}
              onStart={handleStartQuizAttempt}
              onStartVoiceMode={(flow ?? resolvedInitialFlow)?.voice_mode_available && intro?.can_enter_quiz === true
                ? handleStartVoiceMode : undefined}
              onBackHome={() => navigate('/home')}
            />
          </PageContainer>
        </div>
      </ThemeContextProvider>
    );
  }

  if (!questions.length) {
    return <QuestionFlowEmptyState flowName={flowName} />;
  }

  if (!questionToRender) {
    return (
      <ThemeContextProvider value={dynamicThemeContext}>
        {themeLayer}
        <QuestionFlowLoadingState frosted={Boolean(themeProgress)} />
      </ThemeContextProvider>
    );
  }

  return (
    <ThemeContextProvider value={dynamicThemeContext}>
    {themeLayer}
    <QuestionLayout
      {...getTestId(`quiz-${flowName}`)}
      progress={progressValue}
      flowName={flow?.title || flow?.name || flowName}
      flowIdentifier={flow?.name || flowName}
      courseId={courseId}
      theme={flow?.theme}
      themeProgress={themeProgress}
      themeReady={isThemeReady}
    >
      {autosaveError ? (
        <div className="mx-auto w-full max-w-3xl px-4 pb-2">
          {/* Frosted so the red text stays legible over a dark theme backdrop. */}
          <div className="rounded-lg" data-flow-surface="card">
            <Alert variant="destructive" className="pr-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <AlertTitle>Could not save your answer</AlertTitle>
                  <AlertDescription className="mt-1 text-destructive/90">
                    {autosaveError} Please try continuing again.
                  </AlertDescription>
                </div>
                <button
                  type="button"
                  className="text-sm font-medium text-destructive/80 transition-colors hover:text-destructive"
                  onClick={clearAutosaveError}
                >
                  Dismiss
                </button>
              </div>
            </Alert>
          </div>
        </div>
      ) : null}
      <AnswerReferenceContext.Provider value={answerReferenceState.context}>
      <QuestionFlowStage
        isAtFlowEnd={false}
        transitionDirection={transitionDirection}
        question={questionToRender}
        questionLoading={answerReferenceState.loading}
        questionResponse={questionResponse}
        questionFlowItem={questionFlowItem}
        isSubmitting={isSubmitting}
        isSavingResponse={false}
        isLastQuestion={isLastQuestion}
        canGoToPrevious={canGoToPrevious}
        onPrevious={handlePreviousClick}
        onQuestionSubmit={handleQuestionSubmit}
        flow={flow}
        courseId={courseId}
        flowRunId={null}
        currentQuestionIndex={currentQuestionIndex}
        questionIndex={questionIndex}
        upcomingQuestionIds={upcomingQuestionIds}
        debugMetadata={null}
        completionContext={completionContext}
        lastQuestionButtonText={isPassOnlyFlow ? 'Submit Survey' : 'Submit Quiz'}
      />
      </AnswerReferenceContext.Provider>
    </QuestionLayout>
    </ThemeContextProvider>
  );
};

export default QuizFlow;

